import { useState, useEffect } from 'react';
import { clientsAPI } from '../api';
import { Building2, User } from 'lucide-react';

/**
 * ClientPicker - Reusable client selection component
 * Can be used as a modal or embedded in forms
 */
const ClientPicker = ({ onSelect, onClose, showModal = true }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      searchClients();
    } else {
      setClients([]);
    }
  }, [searchQuery]);

  const searchClients = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await clientsAPI.getAll({
        search: searchQuery,
        page: 1,
        limit: 20,
      });
      
      setClients(response.clients || []);
    } catch (err) {
      setError('Failed to search clients');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (client) => {
    onSelect(client);
    if (onClose) onClose();
  };

  const getClientDisplayName = (client) => {
    if (client.client_type === 'company') {
      return client.company_name || 'Unnamed Company';
    }
    return `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unnamed Individual';
  };

  const content = (
    <div className="space-y-4">
      {/* Search Input */}
      <div>
        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
          Search Clients
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Type to search by name or email..."
          className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] focus:border-[var(--ds-accent-primary)]"
          autoFocus
        />
        <p className="text-xs text-gray-500 mt-1">
          Start typing to search for existing clients
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-500">Searching...</p>
        </div>
      )}

      {/* Results */}
      {!loading && searchQuery.trim().length > 0 && clients.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No clients found</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
        </div>
      )}

      {!loading && clients.length > 0 && (
        <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {clients.map((client) => (
            <button
              key={client.id}
              onClick={() => handleSelect(client)}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                    {getClientDisplayName(client).charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {getClientDisplayName(client)}
                      </div>
                      {client.email && (
                        <div className="text-xs text-gray-500">{client.email}</div>
                      )}
                      {client.phone && (
                        <div className="text-xs text-gray-400">{client.phone}</div>
                      )}
                    </div>
                    <div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center justify-center w-6 h-6 ${
                        client.client_type === 'company' 
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                      }`}>
                        {client.client_type === 'company' ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Hint for new client */}
      {searchQuery.trim().length > 2 && clients.length === 0 && !loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Can't find the client?</strong>
          </p>
          <p className="text-xs text-blue-600 mt-1">
            You can create a new client from the Clients page, or continue with "create on the fly" during invoice creation (coming in Phase 4).
          </p>
        </div>
      )}
    </div>
  );

  // If not modal, return content directly
  if (!showModal) {
    return content;
  }

  // Modal wrapper
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Select Client
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1">
          {content}
        </div>
        
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full h-8 px-3 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientPicker;

